import '@digital-realty/ix-icon/ix-icon.js';
import { ARIAMixinStrict } from '@material/web/internal/aria/aria.js';
import { mixinElementInternals } from '@material/web/labs/behaviors/element-internals.js';
import { DEFAULT_TYPEAHEAD_BUFFER_TIME } from '@material/web/menu/internal/menu.js';
import '@material/web/select/filled-select.js';
import { Select } from '@material/web/select/internal/select.js';
import '@material/web/select/outlined-select.js';
import { LitElement, PropertyValues, html, isServer, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { StaticValue, literal, html as staticHtml } from 'lit/static-html.js';
import './selectoption/ix-select-option.js';
import { AriaForwardMixin } from '@digital-realty/ix-shared-fns/aria-forward-mixin.js';
import { spread } from '@open-wc/lit-helpers';

const VALUE = Symbol('value');

const ixSelectBaseClass = AriaForwardMixin(mixinElementInternals(LitElement));

export class IxSelect extends ixSelectBaseClass {
  /** @nocollapse */
  static override shadowRootOptions: ShadowRootInit = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  /** @nocollapse */
  static readonly formAssociated = true;

  @query('.select') component!: Select;

  @property({ type: Number }) minWidth = 0;

  /**
   * Whether or not the drop-down menu should be at least the width of the select element.
   */
  @property({ type: Boolean, reflect: true, attribute: 'wide-menu' }) wideMenu =
    false;

  @property({ type: Number }) truncateLabelBy = 70;

  /**
   * The switch between filled and outlined.
   */
  @property({ type: Boolean, reflect: true }) filled = false;

  /**
   * The floating label for the field.
   */
  @property() label: string = '';

  /**
   * Opens the menu synchronously with no animation.
   */
  @property({ type: Boolean }) quick = false;

  /**
   * Whether or not the select is required.
   */
  @property({ type: Boolean }) required = false;

  /**
   * Disables the select.
   */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /**
   * The error message that replaces supporting text when `error` is true. If
   * `errorText` is an empty string, then the supporting text will continue to
   * show.
   *
   * This error message overrides the error message displayed by
   * `reportValidity()`.
   */
  @property({ type: String, attribute: 'error-text' }) errorText = '';

  /**
   * Conveys additional information below the select, such as how it should
   * be used.
   */
  @property({ type: String, attribute: 'supporting-text' }) supportingText = '';

  /**
   * Gets or sets whether or not the select is in a visually invalid state.
   *
   * This error state overrides the error state controlled by
   * `reportValidity()`.
   */
  @property({ type: Boolean, reflect: true }) error = false;

  /**
   * Forces the menu to be below the select element.
   */
  @property({ type: Boolean, attribute: 'menu-below' }) menuBelow = false;

  /**
   * Text to display in the field. Only set for SSR.
   */
  @property({ type: String, attribute: 'display-text' }) displayText = '';

  /**
   * Name of icon. It is always a string.
   */
  @property({ attribute: 'leading-icon', reflect: true })
  leadingIcon = '';

  /**
   * Name of icon. It is always a string.
   */
  @property({ attribute: 'trailing-icon', reflect: true })
  trailingIcon = '';

  /**
   * Whether or not the underlying md-menu should be position: fixed to display
   * in a top-level manner, or position: absolute.
   *
   * position:fixed is useful for cases where select is inside of another
   * element with stacking context and hidden overflows such as `md-dialog`.
   */
  @property({ attribute: 'menu-positioning' })
  menuPositioning: 'absolute' | 'fixed' | 'popover' = 'absolute';

  /**
   * The max time between the keystrokes of the typeahead select / menu behavior
   * before it clears the typeahead buffer.
   */
  @property({ type: Number, attribute: 'typeahead-delay' })
  typeaheadDelay = DEFAULT_TYPEAHEAD_BUFFER_TIME;

  /**
   * The index of the currently selected option.
   *
   * Note: For SSR, set `[selected]` on the requested option and `displayText`
   * rather than setting `selectedIndex` setting `selectedIndex` will incur a
   * DOM query.
   */
  @property({ type: Number, attribute: 'selected-index' })
  selectedIndex = -1;

  @state() private nativeError = false;

  @state() private nativeErrorText = '';

  private get hasError() {
    return this.error || this.nativeError;
  }

  private readonly internals = (this as HTMLElement) /* needed for closure */
    .attachInternals();

  private customValidationMessage = '';

  get form() {
    return this.internals.form;
  }

  get labels() {
    return this.internals.labels;
  }

  get name() {
    return this.getAttribute('name') ?? '';
  }

  set name(name: string) {
    this.setAttribute('name', name);
  }

  get validity() {
    this.syncValidity();
    return this.internals.validity;
  }

  get value() {
    this[VALUE] = this.component.value;
    return this[VALUE];
  }

  set value(value: string) {
    if (isServer) return;
    this.component.value = value;
  }

  [VALUE] = '';

  get validationMessage() {
    this.syncValidity();
    return this.internals.validationMessage;
  }

  truncateLabel() {
    const label = this.component.shadowRoot?.getElementById('label');
    if (!label) return;
    const labelContainerWidth =
      label.parentElement?.getBoundingClientRect().width;
    if (!labelContainerWidth) return;
    // Avoid truncating if the value is too short to be visable
    if (labelContainerWidth < this.truncateLabelBy * 2) return;
    label.style.setProperty(
      'max-width',
      `${labelContainerWidth - this.truncateLabelBy}px`
    );
    label.style.setProperty('overflow', 'hidden');
    label.style.setProperty('text-overflow', 'ellipsis');
    label.style.setProperty('white-space', 'nowrap');
  }

  private handleSelection() {
    this.internals.setFormValue(this.component.value);
    this.syncValidity();
    this.truncateLabel();
  }

  private setMenuSize() {
    const select = this.shadowRoot?.querySelector('.select') as HTMLElement;
    const menu = select?.shadowRoot
      ?.querySelector('#listbox')
      ?.shadowRoot?.querySelector('.menu') as HTMLElement;

    if (menu.style) {
      menu.style.zIndex = '102';
      if (this.menuBelow) {
        const selectPosition = select?.getBoundingClientRect();
        const spaceBelow =
          window.innerHeight - (selectPosition.top + selectPosition.height);
        menu.style.maxHeight = `${spaceBelow * 0.8}px`;
        menu.style.insetBlockEnd = 'initial';
        menu.style.maxWidth = `${this.component.offsetWidth}px`;
      } else {
        // Set max-height to 70vh to prevent menus with many options from overflowing the viewport which
        // can cause some options to become inaccessible: https://telxapp.atlassian.net/browse/IXUAT-9459
        menu.style.maxHeight = '70vh';
      }
    }
    if (this.wideMenu) {
      this.minWidth = this.component.offsetWidth;
    }
  }

  async getUpdateComplete(): Promise<boolean> {
    await super.getUpdateComplete();
    await this.component.updateComplete;
    this.setMenuSize();
    this.internals.setFormValue(this.component.value);
    return true;
  }

  protected override async updated(changed: PropertyValues<Select>) {
    if (changed.has('required')) {
      this.syncValidity();
    }

    await this.getUpdateComplete();
  }

  private getErrorText() {
    return this.error ? this.errorText : this.nativeErrorText;
  }

  private async syncValidity() {
    const valueMissing = this.required && !this.component.value;
    const customError = !!this.customValidationMessage;
    const validationMessage =
      this.customValidationMessage ||
      (valueMissing && this.getRequiredValidationMessage()) ||
      '';

    this.internals.setValidity(
      { valueMissing, customError },
      validationMessage,
      this.component
    );
  }

  // Returns the platform `<select>` validation message for i18n.
  // eslint-disable-next-line class-methods-use-this
  private getRequiredValidationMessage() {
    const select = document.createElement('select');
    select.required = true;
    return select.validationMessage;
  }

  private handleResize = () => {
    this.minWidth = 0;
  };

  checkValidity() {
    this.syncValidity();
    return this.internals.checkValidity();
  }

  reportValidity() {
    return this.component.reportValidity();
  }

  setCustomValidity(error: string) {
    this.customValidationMessage = error;
    this.syncValidity();
  }

  focus() {
    (
      this.component.shadowRoot?.querySelector('.field') as HTMLFormElement
    )?.focus();
  }

  // eslint-disable-next-line class-methods-use-this
  onBlur(e: FocusEvent) {
    // Prevent blur event from bubbling on focus in (when focus switches to options menu)
    const el = e.relatedTarget as HTMLElement;
    if (el?.tagName === 'IX-SELECT-OPTION') {
      e.stopPropagation();
    }
  }

  /**
   * Reset the select to its default value.
   */
  reset() {
    this.component.reset();
    this.internals.setFormValue(this.component.value);
  }

  /** @private */
  formResetCallback() {
    this.reset();
  }

  /** @private */
  formStateRestoreCallback(newState: string) {
    this.value = newState;
  }

  override connectedCallback() {
    super.connectedCallback();
    if (!isServer && this.wideMenu) {
      window.addEventListener('resize', this.handleResize);
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (!isServer && this.wideMenu) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  override render() {
    const tag: StaticValue = this.filled
      ? literal`md-filled-select`
      : literal`md-outlined-select`;

    const ariaLabel = this.label || (this as ARIAMixinStrict).ariaLabel;

    return staticHtml`<${tag}
        ?disabled=${this.disabled}
        ?quick=${this.quick}
        ?error=${this.hasError}
        ?required=${this.required}
        ?has-leading-icon=${this.leadingIcon.length > 0}
        aria-label=${ariaLabel || nothing}
        tabindex=${ifDefined(this.disabled ? undefined : '0')}
        menu-positioning=${this.menuPositioning}
        typeahead-delay=${this.typeaheadDelay}
        supporting-text=${this.supportingText}
        error-text=${this.getErrorText()}
        selected-index=${ifDefined(
          this.selectedIndex === -1 ? undefined : this.selectedIndex
        )}
        display-text=${this.displayText}
        label=${this.label}
        name=${this.name}
        @request-selection=${this.handleSelection}
        @opening=${() => {
          this.setMenuSize();
          this.dispatchEvent(
            new CustomEvent('selectMenu', {
              bubbles: true,
              composed: true,
              detail: { value: 'open' },
            })
          );
        }}
        @closing=${() => {
          this.dispatchEvent(
            new CustomEvent('selectMenu', {
              bubbles: true,
              composed: true,
              detail: { value: 'close' },
            })
          );
        }}
        class="select"
        style="min-width:${this.minWidth}px"
        @focus=${this.focus}
        @blur=${this.onBlur}
        ${spread(this.ariaAttributes)}
      >
        <slot></slot>
          ${
            this.leadingIcon
              ? html`<ix-icon slot="leading-icon">${this.leadingIcon}</ix-icon>`
              : nothing
          }
          ${
            this.trailingIcon
              ? html`<ix-icon slot="trailing-icon"
                  >${this.trailingIcon}</ix-icon
                >`
              : nothing
          }
         <slot slot="aria-describedby" name="aria-describedby"></slot>
      </${tag}>`;
  }
}
